/**
 * POKÉMON IMAGE RESOLVER
 * 
 * SINGLE SOURCE OF TRUTH for all Pokémon images across entire application.
 * 
 * Rules:
 * - NO component may build image URLs manually
 * - NO hardcoded sprite paths
 * - NO trying/guessing/fallback paths
 * - ALL pages must use this function
 * - One function only - used everywhere
 * - Reads from pokemonImageMap.json ONLY
 * 
 * Usage:
 *   import { getPokemonImage } from '../utils/pokemonImageResolver';
 *   const imageUrl = getPokemonImage(pokemonId, 'default');
 *   const shinyUrl = getPokemonImage(pokemonId, 'shiny');
 *   const megaUrl = getPokemonImage(pokemonId, 'mega_x');
 */

import React from 'react';

const PLACEHOLDER = '/images/pokemon-spirit/000.png';
const MAP_URL = '/pokemonImageMap.json';

// Cache for loaded image map
let imageMapCache = null;
let loadingPromise = null;
let mapLoadListeners = new Set(); // For notifying components when map loads

/**
 * Load image map from JSON file (cached)
 * @returns {Promise<Object>} - Image map object
 */
const loadImageMap = async () => {
  // Return cached map if already loaded
  if (imageMapCache) {
    return imageMapCache;
  }
  
  // Return existing loading promise if already loading
  if (loadingPromise) {
    return loadingPromise;
  }
  
  // Start loading
  loadingPromise = fetch(MAP_URL)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load image map: ${response.status}`);
      }
      return response.json();
    })
    .then(map => {
      imageMapCache = map;
      // Notify all listeners that map is loaded
      mapLoadListeners.forEach(listener => listener());
      mapLoadListeners.clear(); // Clear listeners after notifying
      return map;
    })
    .catch(error => {
      console.error('Error loading pokemonImageMap.json:', error);
      imageMapCache = {}; // Return empty map on error
      return imageMapCache;
    })
    .finally(() => {
      loadingPromise = null;
    });
  
  return loadingPromise;
};

/**
 * Get image map (synchronous access with fallback)
 * @returns {Object} - Image map object (may be empty if not loaded yet)
 */
const getImageMap = () => {
  return imageMapCache || {};
};

/**
 * Extract variant from Pokemon ID or object
 * @param {string|number|object} pokemon - Pokemon ID string/number or Pokemon object
 * @returns {string|null} - Variant name or null
 */
const extractVariantFromPokemon = (pokemon) => {
  if (!pokemon) return null;
  
  // Handle string/number input (Pokemon ID)
  if (typeof pokemon === 'string' || typeof pokemon === 'number') {
    const idStr = String(pokemon);
    // Extract variant from ID like "3-Mega", "6-Mega_X", "25-Gigantamax"
    if (idStr.includes('-')) {
      const parts = idStr.split('-');
      if (parts.length > 1) {
        const variantPart = parts.slice(1).join('-').toLowerCase();
        
        // Normalize variant names
        if (variantPart.includes('mega_x') || variantPart === 'mega-x') return 'mega_x';
        if (variantPart.includes('mega_y') || variantPart === 'mega-y') return 'mega_y';
        if (variantPart.includes('mega')) return 'mega';
        if (variantPart.includes('gigantamax') || variantPart.includes('gmax')) return 'gigantamax';
        if (variantPart.includes('alola') || variantPart.includes('alolan')) return 'alolan';
        if (variantPart.includes('galar') || variantPart.includes('galarian')) return 'galarian';
        if (variantPart.includes('hisui') || variantPart.includes('hisuian')) return 'hisuian';
        if (variantPart.includes('paldea') || variantPart.includes('paldean')) return 'paldean';
        
        return variantPart;
      }
    }
    return null;
  }
  
  // Handle object input - check Pokemon ID and name
  const pokemonId = pokemon.id || 
                   pokemon.Pokemon_ID || 
                   pokemon.pokemon_id ||
                   pokemon.speciesId ||
                   pokemon.species_id ||
                   pokemon.name_species_id ||
                   null;
  
  if (pokemonId) {
    const idStr = String(pokemonId);
    if (idStr.includes('-')) {
      const parts = idStr.split('-');
      if (parts.length > 1) {
        const variantPart = parts.slice(1).join('-').toLowerCase();
        if (variantPart.includes('mega_x')) return 'mega_x';
        if (variantPart.includes('mega_y')) return 'mega_y';
        if (variantPart.includes('mega')) return 'mega';
        if (variantPart.includes('gigantamax') || variantPart.includes('gmax')) return 'gigantamax';
        if (variantPart.includes('alola') || variantPart.includes('alolan')) return 'alolan';
        if (variantPart.includes('galar') || variantPart.includes('galarian')) return 'galarian';
        if (variantPart.includes('hisui') || variantPart.includes('hisuian')) return 'hisuian';
        if (variantPart.includes('paldea') || variantPart.includes('paldean')) return 'paldean';
      }
    }
  }
  
  // Check name for variant keywords
  // Priority: English name first (more likely to have variant info), then Thai
  const pokemonName = pokemon.Pokemon_Name_EN || 
                     pokemon.name_english ||
                     pokemon.name || 
                     pokemon.pokemon_name || 
                     pokemon.Pokemon_Name_TH ||
                     pokemon.name_th || 
                     '';
  
  if (pokemonName) {
    const nameLower = String(pokemonName).toLowerCase().trim();
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`[VARIANT DETECTION] Checking name: "${pokemonName}" (lowercase: "${nameLower}")`);
    }
    
    // Check for variants in parentheses first (e.g., "Rattata (Alolan)", "Raticate (Alolan)")
    // This is the most reliable pattern
    if (nameLower.includes('(alolan)') || nameLower.includes('(alola)')) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[VARIANT DETECTION] Found Alolan variant from parentheses');
      }
      return 'alolan';
    }
    if (nameLower.includes('(galarian)') || nameLower.includes('(galar)')) {
      return 'galarian';
    }
    if (nameLower.includes('(hisuian)') || nameLower.includes('(hisui)')) {
      return 'hisuian';
    }
    if (nameLower.includes('(paldean)') || nameLower.includes('(paldea)')) {
      return 'paldean';
    }
    
    // Check for variants at the start of name (e.g., "Alolan Rattata", "Galarian Corsola")
    // This must come BEFORE general includes check to avoid false positives
    const words = nameLower.split(/\s+/);
    if (words.length > 1) {
      const firstWord = words[0];
      if (firstWord === 'alolan' || firstWord === 'alola') {
        if (process.env.NODE_ENV === 'development') {
          console.log('[VARIANT DETECTION] Found Alolan variant from start of name');
        }
        return 'alolan';
      }
      if (firstWord === 'galarian' || firstWord === 'galar') {
        return 'galarian';
      }
      if (firstWord === 'hisuian' || firstWord === 'hisui') {
        return 'hisuian';
      }
      if (firstWord === 'paldean' || firstWord === 'paldea') {
        return 'paldean';
      }
    }
    
    // Check for variants in the name (but not if it's "normal" variant)
    // This is less reliable but catches cases like "Alolan Rattata"
    if (nameLower.includes('alolan') && !nameLower.includes('normal')) {
      // Make sure it's not part of a word like "alolanpokemon"
      const alolanIndex = nameLower.indexOf('alolan');
      const beforeChar = nameLower[alolanIndex - 1] || '';
      const afterChar = nameLower[alolanIndex + 6] || '';
      // Check if alolan is a separate word (has space before or after, or at start)
      if (alolanIndex === 0 || beforeChar === ' ' || beforeChar === '-' || afterChar === ' ' || afterChar === '-') {
        if (process.env.NODE_ENV === 'development') {
          console.log('[VARIANT DETECTION] Found Alolan variant from includes check');
        }
        return 'alolan';
      }
    }
    if (nameLower.includes('galarian') && !nameLower.includes('normal')) {
      const galarianIndex = nameLower.indexOf('galarian');
      const beforeChar = nameLower[galarianIndex - 1] || '';
      const afterChar = nameLower[galarianIndex + 8] || '';
      if (galarianIndex === 0 || beforeChar === ' ' || beforeChar === '-' || afterChar === ' ' || afterChar === '-') {
        return 'galarian';
      }
    }
    
    // Check for Mega variants (must be before general "mega" check)
    if (nameLower.includes('mega x') || nameLower.includes('mega-x')) return 'mega_x';
    if (nameLower.includes('mega y') || nameLower.includes('mega-y')) return 'mega_y';
    if (nameLower.includes('mega ')) return 'mega';
    // Check for Gigantamax
    if (nameLower.includes('gigantamax') || nameLower.includes('gmax')) return 'gigantamax';
  }
  
  return null;
};

/**
 * Extract Pokemon ID from name using mapping
 * @param {object} pokemon - Pokemon object
 * @returns {string|null} - Pokemon ID or null
 */
const extractIdFromName = (pokemon) => {
  if (!pokemon) return null;
  
  // Try to get Pokemon name
  const pokemonName = pokemon.pokemon_name ||
                     pokemon.name ||
                     pokemon.name_english ||
                     pokemon.Pokemon_Name_EN ||
                     pokemon.name_th ||
                     pokemon.Pokemon_Name_TH ||
                     '';
  
  if (!pokemonName) return null;
  
  // Import pokemonNameMap dynamically (it's loaded from API)
  // For now, we'll need to access it from the module
  // This is a fallback - ideally we should use dex_number/base_id
  return null; // Will be handled by using dex_number/base_id instead
};

/**
 * Extract Pokemon ID from various input formats
 * @param {string|number|object} pokemon - Pokemon ID string/number or Pokemon object
 * @returns {string|null} - Base Pokemon ID or null
 */
const extractPokemonId = (pokemon) => {
  if (!pokemon) return null;
  
  // Handle string/number input (Pokemon ID)
  if (typeof pokemon === 'string' || typeof pokemon === 'number') {
    const idStr = String(pokemon);
    // Extract base ID (e.g., "6" from "6-Mega_X" or "6" from "6")
    const baseId = idStr.split(/[-_]/)[0].replace(/^0+/, '') || '1';
    return baseId;
  }
  
  // Handle object input - extract ID from various fields
  // Priority: dex_number/base_id (numeric) > id fields > name lookup
  let pokemonId = null;
  
  // First, try to get numeric dex_number or base_id (most reliable for image map)
  if (pokemon.dex_number) {
    pokemonId = pokemon.dex_number;
  } else if (pokemon.base_id) {
    pokemonId = pokemon.base_id;
  } else {
    // Fallback to other ID fields
    pokemonId = pokemon.id || 
               pokemon.Pokemon_ID || 
               pokemon.pokemon_id ||
               pokemon.speciesId ||
               pokemon.species_id ||
               pokemon.name_species_id ||
               (pokemon.pokemon && (pokemon.pokemon.id || pokemon.pokemon.Pokemon_ID || pokemon.pokemon.pokemon_id)) ||
               null;
  }
  
  if (!pokemonId) return null;
  
  // Extract base ID from complex IDs
  const idStr = String(pokemonId);
  
  // If it's a numeric string, check if it's a valid Pokemon ID
  if (/^\d+$/.test(idStr)) {
    const numId = parseInt(idStr, 10);
    // If number is too large (> 2000), it's probably not a Pokemon ID
    // This could be a team ID, ranking ID, or other identifier
    if (numId > 2000) {
      // Try to get dex_number or base_id from original object
      // These should be the actual Pokemon dex numbers
      if (pokemon.dex_number && pokemon.dex_number <= 2000) {
        return String(pokemon.dex_number).replace(/^0+/, '') || '1';
      }
      if (pokemon.base_id && pokemon.base_id <= 2000) {
        return String(pokemon.base_id).replace(/^0+/, '') || '1';
      }
      // Can't determine valid Pokemon ID
      return null;
    }
    // Valid Pokemon ID (1-2000)
    const baseId = idStr.split(/[-_]/)[0].replace(/^0+/, '') || '1';
    return baseId;
  }
  
  // If it's a name (like "empoleon", "altaria"), we can't use it directly
  // The map file uses numeric IDs, so we need to convert name to ID
  // For now, return null - the component should provide dex_number/base_id
  if (/^[a-z_]+$/i.test(idStr)) {
    // Try to get dex_number or base_id from object
    if (pokemon.dex_number) {
      return String(pokemon.dex_number).replace(/^0+/, '') || '1';
    }
    if (pokemon.base_id) {
      return String(pokemon.base_id).replace(/^0+/, '') || '1';
    }
    return null; // Can't convert name to ID without mapping
  }
  
  // Otherwise, extract base ID normally
  const baseId = idStr.split(/[-_]/)[0].replace(/^0+/, '') || '1';
  return baseId;
};

/**
 * Normalize variant name
 * @param {string} variant - Variant name
 * @returns {string} - Normalized variant key
 */
const normalizeVariant = (variant) => {
  if (!variant || variant === 'normal' || variant === 'default') {
    return 'default';
  }
  
  // Normalize variant names
  const normalized = variant.toLowerCase().trim();
  
  // Map common variants
  if (normalized === 'shiny') return 'shiny';
  if (normalized === 'mega_x' || normalized === 'mega-x' || normalized === 'mega x') return 'mega_x';
  if (normalized === 'mega_y' || normalized === 'mega-y' || normalized === 'mega y') return 'mega_y';
  if (normalized === 'mega') return 'mega';
  if (normalized === 'gigantamax' || normalized === 'gmax') return 'gigantamax';
  if (normalized === 'alolan' || normalized === 'alola') return 'alolan';
  if (normalized === 'galarian' || normalized === 'galar') return 'galarian';
  if (normalized === 'hisuian' || normalized === 'hisui') return 'hisuian';
  if (normalized === 'paldean' || normalized === 'paldea') return 'paldean';
  
  return normalized;
};

/**
 * Get Pokémon image URL (synchronous version - uses cached map)
 * 
 * @param {string|number|object} pokemonId - Pokemon ID string/number or Pokemon object
 * @param {string} variant - Variant name: 'default', 'shiny', 'mega_x', 'mega_y', 'mega', 'gigantamax', 'alolan', 'galarian', 'hisuian', 'paldean'
 * @returns {string} - Image URL or placeholder
 * 
 * Examples:
 *   getPokemonImage(25) → Pikachu default
 *   getPokemonImage(6, 'mega_x') → Charizard Mega X
 *   getPokemonImage(25, 'shiny') → Shiny Pikachu
 *   getPokemonImage(9999) → placeholder
 */
export const getPokemonImage = (pokemonId, variant = 'default') => {
  // Extract base Pokemon ID
  const baseId = extractPokemonId(pokemonId);
  
  // Debug logging (remove in production if needed)
  if (process.env.NODE_ENV === 'development' && !baseId && pokemonId) {
    console.warn('[IMAGE RESOLVER] Could not extract Pokemon ID from:', pokemonId);
  }
  
  // Return placeholder if no ID found
  if (!baseId) {
    return PLACEHOLDER;
  }
  
  // If variant is 'default', try to extract variant from Pokemon ID/object
  // BUT only if Pokemon ID/name clearly indicates a variant (e.g., "3-Mega", "Mega Venusaur")
  // If Pokemon is clearly normal (just ID "3" or name "Venusaur"), use default
  let actualVariant = variant;
  if (variant === 'default') {
    const extractedVariant = extractVariantFromPokemon(pokemonId);
    
    // Debug logging for variant detection
    if (process.env.NODE_ENV === 'development' && extractedVariant) {
      const pokemonObj = (typeof pokemonId === 'object') ? pokemonId : null;
      const pokemonName = pokemonObj ? 
        (pokemonObj.Pokemon_Name_EN || pokemonObj.name || pokemonObj.pokemon_name || '') : 
        '';
      if (pokemonName) {
        console.log(`[IMAGE RESOLVER] Detected variant "${extractedVariant}" from Pokemon name: "${pokemonName}", baseId: ${baseId}`);
      }
    }
    
    // Only use extracted variant if it was explicitly found (not null)
    // This ensures normal Pokemon (without variant in ID/name) use default form
    if (extractedVariant) {
      actualVariant = extractedVariant;
    }
    // Otherwise keep 'default' to use normal form
  }
  
  // Get image map (may be empty if not loaded yet - will retry on next render)
  const imageMap = getImageMap();
  
  // If map not loaded yet, trigger load and return placeholder for now
  if (!imageMap || Object.keys(imageMap).length === 0) {
    // Try to load map asynchronously (for next render)
    if (!loadingPromise) {
      loadImageMap().catch(() => {
        // Silently fail - will retry later
      });
    }
    // Return placeholder until map loads
    return PLACEHOLDER;
  }
  
  // Normalize variant BEFORE checking map
  const normalizedVariant = normalizeVariant(actualVariant);
  
  // Debug logging for variant normalization
  if (process.env.NODE_ENV === 'development' && actualVariant !== 'default') {
    console.log(`[IMAGE RESOLVER] Variant: ${actualVariant} → normalized: ${normalizedVariant} for baseId: ${baseId}`);
  }
  
  // Get Pokemon entry from map
  const pokemonEntry = imageMap[baseId];
  
  // If Pokemon not in map, try fallback based on detected variant
  if (!pokemonEntry) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[IMAGE RESOLVER] Pokemon not found in map for baseId:', baseId, 'variant:', normalizedVariant, 'actualVariant:', actualVariant, '- trying variant-specific fallback');
      console.log(`[IMAGE RESOLVER] Checking variant conditions - normalizedVariant: "${normalizedVariant}", actualVariant: "${actualVariant}"`);
    }
    
    // For Alolan variants, try Alolan-specific path first (this fixes Alolan Rattata/Raticate issue)
    // Check both normalizedVariant and actualVariant to be safe
    const isAlolan = normalizedVariant === 'alolan' || actualVariant === 'alolan' || 
                     normalizedVariant.includes('alolan') || actualVariant.includes('alolan');
    
    if (isAlolan) {
      const alolanPath = `/images/pokemon-spirit/${baseId}-Alolan.png`;
      if (process.env.NODE_ENV === 'development') {
        console.log(`[IMAGE RESOLVER] ✅ Using Alolan variant path: ${alolanPath}`);
      }
      return alolanPath;
    }
    
    if (normalizedVariant === 'galarian' || actualVariant === 'galarian') {
      return `/images/pokemon-spirit/${baseId}-Galarian.png`;
    }
    if (normalizedVariant === 'hisuian' || actualVariant === 'hisuian') {
      return `/images/pokemon-spirit/${baseId}-Hisuian.png`;
    }
    if (normalizedVariant === 'paldean' || actualVariant === 'paldean') {
      return `/images/pokemon-spirit/${baseId}-Paldean.png`;
    }
    
    // Fallback priority:
    // 1. Try pokemon_images_ingame with variant suffix
    // 2. Try pokemon_images_ingame default
    if (normalizedVariant === 'shiny' || actualVariant === 'shiny') {
      return `/images/pokemon_images_ingame/ingame-${baseId}-s.png`;
    }
    
    // Return pokemon_images_ingame path (error handler will try both .png and .webp)
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[IMAGE RESOLVER] No variant detected, using default fallback: /images/pokemon_images_ingame/ingame-${baseId}.png`);
    }
    return `/images/pokemon_images_ingame/ingame-${baseId}.png`;
  }
  
  // Get image URL for variant - prioritize exact match
  let imageUrl = pokemonEntry[normalizedVariant];
  
  // CRITICAL FIX: If we got a default image, check if it's actually a variant
  // This fixes Butterfree showing Gigantamax when requesting default
  if (imageUrl && normalizedVariant === 'default' && actualVariant === 'default') {
    // Check if default image path contains variant indicators
    if (imageUrl.includes('-Gigantamax') || imageUrl.includes('-Mega') || 
        imageUrl.includes('/12-Gigantamax') || imageUrl.includes('/12-Mega')) {
      // Default is a variant - don't use it, try pokemon_images_ingame instead
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[IMAGE RESOLVER] Default image is a variant (${imageUrl}) for baseId ${baseId}, using pokemon_images_ingame fallback instead`);
      }
      imageUrl = null; // Force fallback to pokemon_images_ingame
    }
  }
  
  // Special case: If variant is 'alolan' but entry doesn't exist, check if Pokemon ID includes "-Alolan"
  // or if Pokemon name clearly indicates Alolan variant
  if (!imageUrl && normalizedVariant === 'alolan') {
    // Try with hyphen format: "19-Alolan" or underscore format: "19_alolan"
    const pokemonObj = (typeof pokemonId === 'object') ? pokemonId : null;
    const pokemonIdStr = pokemonObj ? 
      (pokemonObj.id || pokemonObj.Pokemon_ID || pokemonObj.pokemon_id || '') : 
      String(pokemonId || '');
    
    if (pokemonIdStr.includes('-Alolan') || pokemonIdStr.includes('_alolan') || pokemonIdStr.includes('_Alolan')) {
      // This is an Alolan variant Pokemon - try to find in map or fallback
      // The base ID extraction should have removed the variant part
      // So we need to check if there's an Alolan-specific entry
      const alolanEntry = imageMap[`${baseId}-Alolan`] || imageMap[`${baseId}_alolan`] || imageMap[`${baseId}_Alolan`];
      if (alolanEntry && alolanEntry.default) {
        imageUrl = alolanEntry.default;
      }
    }
  }
  
  // CRITICAL FIX: Check if the imageUrl is a variant when we requested default
  // This fixes Butterfree showing Gigantamax when requesting default
  if (imageUrl && normalizedVariant === 'default' && actualVariant === 'default') {
    // Check if default image is actually a variant (like Butterfree's default being Gigantamax)
    if (imageUrl.includes('-Gigantamax') || imageUrl.includes('-Mega') || 
        imageUrl.includes('-Alolan') || imageUrl.includes('-Galarian') || 
        imageUrl.includes('-Hisuian') || imageUrl.includes('-Paldean')) {
      // Default is a variant - don't use it, try pokemon_images_ingame instead
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[IMAGE RESOLVER] Default image is a variant (${imageUrl}) for baseId ${baseId}, using pokemon_images_ingame fallback instead`);
      }
      imageUrl = null; // Force fallback
    }
  }
  
  // If variant doesn't exist, fallback logic:
  if (!imageUrl) {
    // If user explicitly requested a variant (not 'default'), don't fallback to default
    // This prevents showing wrong variant when specific variant is requested
    // BUT: if it's an Alolan variant and map doesn't have it, try pokemon_images_ingame
    if (variant !== 'default' && actualVariant !== 'default') {
      // Specific variant requested but doesn't exist - try pokemon_images_ingame fallback
      if (normalizedVariant === 'shiny') {
        return `/images/pokemon_images_ingame/ingame-${baseId}-s.png`;
      }
      // For Alolan variants, try ingame folder with -alolan suffix
      if (normalizedVariant === 'alolan') {
        return `/images/pokemon-spirit/${baseId}-Alolan.png`;
      }
      return `/images/pokemon_images_ingame/ingame-${baseId}.png`;
    }
    
    // Only fallback to default if user didn't specify a variant
    // BUT: Don't fallback to default if we detected a variant from the name
    // This prevents Alolan Rattata from showing normal Rattata, or Butterfree from showing Gigantamax
    if (actualVariant !== 'alolan' && actualVariant !== 'galarian' && actualVariant !== 'hisuian' && actualVariant !== 'paldean' && 
        actualVariant !== 'gigantamax' && actualVariant !== 'mega' && actualVariant !== 'mega_x' && actualVariant !== 'mega_y') {
      // Check if default is actually a variant (like Butterfree's default being Gigantamax)
      const defaultUrl = pokemonEntry['default'];
      if (defaultUrl && (defaultUrl.includes('-Gigantamax') || defaultUrl.includes('-Mega'))) {
        // Default is a variant - don't use it, try pokemon_images_ingame instead
        // This fixes Butterfree showing Gigantamax instead of normal form
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[IMAGE RESOLVER] Default image is a variant (${defaultUrl}) for baseId ${baseId}, using pokemon_images_ingame fallback instead`);
        }
        return `/images/pokemon_images_ingame/ingame-${baseId}.png`;
      }
      imageUrl = defaultUrl;
    } else {
      // Detected variant from name but variant entry doesn't exist in map - try variant-specific path
      // This ensures Alolan Rattata uses Alolan image instead of normal Rattata
      if (normalizedVariant === 'alolan') {
        imageUrl = `/images/pokemon-spirit/${baseId}-Alolan.png`;
        if (process.env.NODE_ENV === 'development') {
          console.log(`[IMAGE RESOLVER] Using Alolan variant path for baseId ${baseId}: ${imageUrl}`);
        }
      } else if (normalizedVariant === 'galarian') {
        imageUrl = `/images/pokemon-spirit/${baseId}-Galarian.png`;
      } else if (normalizedVariant === 'hisuian') {
        imageUrl = `/images/pokemon-spirit/${baseId}-Hisuian.png`;
      } else if (normalizedVariant === 'paldean') {
        imageUrl = `/images/pokemon-spirit/${baseId}-Paldean.png`;
      } else {
        // Other variants detected - don't fallback to default
        // Try pokemon_images_ingame instead
        imageUrl = `/images/pokemon_images_ingame/ingame-${baseId}.png`;
      }
    }
  }
  
  // If still no image from map, try pokemon_images_ingame fallback
  if (!imageUrl) {
    if (normalizedVariant === 'shiny') {
      return `/images/pokemon_images_ingame/ingame-${baseId}-s.png`;
    }
    return `/images/pokemon_images_ingame/ingame-${baseId}.png`;
  }
  
  // Ensure imageUrl starts with /images/
  if (!imageUrl.startsWith('/images/')) {
    imageUrl = `/images/${imageUrl}`;
  }
  
  return imageUrl;
};

/**
 * Initialize image map (call this once at app startup)
 * @returns {Promise<void>}
 */
export const initializeImageMap = async () => {
  await loadImageMap();
};

/**
 * Hook to force re-render when image map loads
 * @returns {boolean} - True when map is loaded
 */
export const useImageMapReady = () => {
  const [isReady, setIsReady] = React.useState(!!imageMapCache);
  
  React.useEffect(() => {
    if (imageMapCache) {
      setIsReady(true);
      return;
    }
    
    // Add listener for when map loads
    const listener = () => setIsReady(true);
    mapLoadListeners.add(listener);
    
    // Try to load map
    loadImageMap().catch(() => {});
    
    return () => {
      mapLoadListeners.delete(listener);
    };
  }, []);
  
  return isReady;
};

/**
 * Get Pokémon image URL (object parameter style for backward compatibility)
 * 
 * @param {string|number|object} pokemon - Pokemon ID string/number or Pokemon object
 * @param {object} options - Image options
 * @param {string} options.variant - Variant name
 * @returns {string} - Image URL or placeholder
 */
export const getPokemonImageWithOptions = (pokemon, options = {}) => {
  const variant = options.variant || options.variant || 'default';
  return getPokemonImage(pokemon, variant);
};

/**
 * Generate fallback image paths for a Pokemon
 * Priority order:
 * 1. pokemon-spirit folder (primary source, may be updated later)
 * 2. pokemon_images_ingame folder (fallback for Ho-Oh, etc.)
 * 3. pokemon folder (legacy)
 * 4. Placeholder
 * 
 * @param {string|number} baseId - Base Pokemon ID
 * @param {string} currentPath - Current image path that failed
 * @param {string} variant - Variant (for shiny suffix)
 * @returns {string[]} - Array of fallback paths in priority order
 */
const generateFallbackPaths = (baseId, currentPath, variant = 'default') => {
  const fallbacks = [];
  const baseIdStr = String(baseId);
  const isShiny = variant === 'shiny' || variant.includes('shiny');
  
  // Extract Pokemon ID from current path if possible
  let pokemonId = baseIdStr;
  if (currentPath) {
    // Try to extract ID from path like /images/pokemon-spirit/250.png or /images/pokemon-spirit/250-Mega.png
    const match = currentPath.match(/pokemon-spirit\/([\d-]+)\.(png|webp)/) ||
                  currentPath.match(/pokemon_images_ingame\/ingame-([\d-]+)/);
    if (match) {
      pokemonId = match[1];
    }
  }
  
  // Priority 1: Try pokemon-spirit (may have been updated later)
  // Only add if current path is NOT from pokemon-spirit (to avoid retrying same path)
  if (!currentPath || !currentPath.includes('pokemon-spirit')) {
    if (isShiny) {
      fallbacks.push(`/images/pokemon-spirit/${pokemonId}_shiny.png`);
      fallbacks.push(`/images/pokemon-spirit/${pokemonId}_shiny.webp`);
    }
    fallbacks.push(`/images/pokemon-spirit/${pokemonId}.png`);
    fallbacks.push(`/images/pokemon-spirit/${pokemonId}.webp`);
  }
  
  // Priority 2: Try pokemon_images_ingame (for Ho-Oh and others)
  // Only add if current path is NOT from pokemon_images_ingame
  if (!currentPath || !currentPath.includes('pokemon_images_ingame')) {
    if (isShiny) {
      fallbacks.push(`/images/pokemon_images_ingame/ingame-${pokemonId}-s.png`);
      fallbacks.push(`/images/pokemon_images_ingame/ingame-${pokemonId}-s.webp`);
    }
    fallbacks.push(`/images/pokemon_images_ingame/ingame-${pokemonId}.png`);
    fallbacks.push(`/images/pokemon_images_ingame/ingame-${pokemonId}.webp`);
  }
  
  // Priority 3: Try legacy pokemon folder
  const paddedId = baseIdStr.padStart(4, '0');
  fallbacks.push(`/images/pokemon/${paddedId}.png`);
  fallbacks.push(`/images/pokemon/${paddedId}.webp`);
  
  // Priority 4: Placeholder as last resort
  fallbacks.push(PLACEHOLDER);
  
  // Remove duplicates and current path
  return fallbacks.filter((path, index, self) => 
    path !== currentPath && self.indexOf(path) === index
  );
};

// WeakMap to store fallback state per image element
const imageFallbackState = new WeakMap();

/**
 * Create React image error handler with fallback chain
 * 
 * Priority order:
 * 1. pokemon-spirit folder (primary source)
 * 2. pokemon_images_ingame folder (fallback for Ho-Oh, etc.)
 * 3. pokemon folder (legacy)
 * 4. Placeholder
 * 
 * @param {string|number|object} pokemon - Pokemon ID or object
 * @param {object} options - Image options
 * @param {string} options.variant - Variant name
 * @returns {function} - onError handler function
 */
export const createPokemonImageErrorHandler = (pokemon, options = {}) => {
  return function handleImageError(event) {
    const img = event.target;
    const currentSrc = img.src;
    
    // Get or create fallback state for this image element
    let state = imageFallbackState.get(img);
    if (!state) {
      // Extract Pokemon ID
      const baseId = extractPokemonId(pokemon);
      if (!baseId) {
        // No ID available - use placeholder
        img.onerror = null;
        img.src = PLACEHOLDER;
        return;
      }
      
      // Get variant from options
      const variant = options.variant || 'default';
      
      // Generate fallback paths
      const fallbacks = generateFallbackPaths(baseId, currentSrc, variant);
      state = {
        attemptCount: 0,
        fallbacks: fallbacks
      };
      imageFallbackState.set(img, state);
    }
    
    // Try next fallback
    state.attemptCount++;
    
    if (state.attemptCount <= state.fallbacks.length) {
      const nextPath = state.fallbacks[state.attemptCount - 1];
      // Convert to full URL if needed
      if (nextPath.startsWith('/images/')) {
        const fullUrl = window.location.origin + nextPath;
        // Only change if different from current to avoid infinite loop
        try {
          const currentUrl = new URL(currentSrc);
          const nextUrl = new URL(fullUrl);
          if (currentUrl.pathname !== nextUrl.pathname) {
            img.src = fullUrl;
            return;
          }
        } catch (e) {
          // URL parsing failed - just try the path
          img.src = fullUrl;
          return;
        }
      }
    }
    
    // All fallbacks exhausted - use placeholder and prevent further errors
    img.onerror = null;
    img.src = PLACEHOLDER;
  };
};

// Export as default for convenience
export default getPokemonImage;
